import { useEffect } from 'react'
import { createPortal } from 'react-dom'

export interface DrawerProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  footer?: React.ReactNode
  position?: 'left' | 'right' | 'top' | 'bottom'
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  closeOnBackdropClick?: boolean
  className?: string
}

const sizeStyles: Record<string, Record<string, string>> = {
  sm: { width: 'max-w-sm', height: 'h-full' },
  md: { width: 'max-w-md', height: 'h-full' },
  lg: { width: 'max-w-lg', height: 'h-full' },
  xl: { width: 'max-w-2xl', height: 'h-full' },
  full: { width: 'w-full', height: 'h-full' },
}

const positionStyles: Record<string, string> = {
  right: 'right-0 top-0 h-full',
  left: 'left-0 top-0 h-full',
  top: 'top-0 left-0 w-full',
  bottom: 'bottom-0 left-0 w-full',
}

const transformStyles: Record<string, Record<string, string>> = {
  right: {
    open: 'translate-x-0',
    closed: 'translate-x-full',
  },
  left: {
    open: 'translate-x-0',
    closed: '-translate-x-full',
  },
  top: {
    open: 'translate-y-0',
    closed: '-translate-y-full',
  },
  bottom: {
    open: 'translate-y-0',
    closed: 'translate-y-full',
  },
}

function Drawer({
  isOpen,
  onClose,
  title,
  children,
  footer,
  position = 'right',
  size = 'lg',
  closeOnBackdropClick = true,
  className = '',
}: DrawerProps) {
  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.removeEventListener('keydown', handleEsc)
    }
  }, [isOpen, onClose])

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose()
    }
  }

  const sizeStyle = sizeStyles[size] || sizeStyles.lg
  const positionStyle = positionStyles[position] || positionStyles.right
  const transformStyle = transformStyles[position] || transformStyles.right

  const drawerContent = (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className={`fixed ${positionStyle} ${sizeStyle.width} ${sizeStyle.height} bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? transformStyle.open : transformStyle.closed
        } ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'drawer-title' : undefined}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          {title && (
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h2 id="drawer-title" className="text-xl font-semibold text-gray-900">
                {title}
              </h2>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close drawer"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {title ? (
              <div className="px-6 py-6">{children}</div>
            ) : (
              children
            )}
          </div>

          {/* Footer */}
          {footer && (
            <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0">
              {footer}
            </div>
          )}
        </div>
      </div>
    </>
  )

  // Render drawer using portal to avoid z-index issues
  return createPortal(drawerContent, document.body)
}

export default Drawer
